import { Injectable } from '@angular/core';
import { MenuList } from './menu.interfaces';
import { IsProjectType } from '../../model/is-project-type';
import { ProjectFinalizationInfoVO, ProjectVO } from '../../model/vo/project.vo';
import { ProjectModel } from '../../model/project.model';
import { UserModel } from '@user/model/user.model';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { StringUtils } from '@shared/util/string-utils';
import { AlertButton } from '@shared/alert/alertButton';
import { OverlayService } from '@shared/overlay/overlay.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { ProjectComponentPresenter } from '../../components/project-component.presenter';
import { SaveProjectService } from '../../components/project/project-save/save-project.service';
import { MarkingSystemDataService } from '../../components/article/manager/marking-system-data.service';
import { RepresentationService } from '../representation.service';
import { SaveCompositeArticleService } from '../save-composite-article.service';
import { CalculationProjectExportService } from '../../components/import-export/service/calculation-project-export.service';
import { ProjectFinalizationService } from '../../finalization/project-finalization.service';
import { Log } from '@shared/debug/log';
import { ToastrService } from 'ngx-toastr';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';

@Injectable({providedIn: 'root'})
export class InternalMenuCreator {


  constructor(
    private overlayService: OverlayService,
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private userModel: UserModel,
    private systemConfigurationService: SystemConfigurationService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private saveProjectService: SaveProjectService,
    private markingSystemDataService: MarkingSystemDataService,
    private representationService: RepresentationService,
    private saveCompositeArticleService: SaveCompositeArticleService,
    private calculationProjectExportService: CalculationProjectExportService,
    private projectFinalizaionService: ProjectFinalizationService,
    private toastrService: ToastrService,
    private localeResourceManager: LocaleResourceManager,
  ) {
  }

  public create(): { id: string, content: MenuList } {
    const items: MenuList = [
      {
        label: 'header.menu.pdm_export',
        action: () => this.pdmExportClickHandler(),
        enabled: this.isPDMExportEnabled(),
        visible: this.isPDMExportVisible(),
        appActionLogId: 'header.menu.pdm_export',
        id: 'header-menu-internal-pdm',
      },
      {
        label: 'header.menu.calculations_export',
        action: () => this.generateCalculationProjectRepresentation(),
        enabled: this.isCalculationsExportEnabled(),
        visible: this.isCalculationsExportVisible(),
        appActionLogId: 'header.menu.calculations_export',
        id: 'header-menu-internal-manual-calculation',
      },
      {
        label: 'header.menu.check_for_duplicates',
        action: () => this.checkForDuplicates(),
        enabled: this.isCheckForDuplicatesEnabled(),
        visible: this.isCheckForDuplicatesVisible(),
        appActionLogId: 'header.menu.check_for_duplicates',
        id: 'header-menu-internal-duplicates',
      },
      {
        label: 'header.menu.reset_for_pdm_export',
        action: () => this.resetForPdmExport(),
        enabled: this.isResetForPdmExportEnabled(),
        visible: this.isVisibleForPdmExportVisible(),
        appActionLogId: 'header.menu.reset_for_pdm_export',
        id: 'header-menu-internal-reset-for-pdm',
      },
    ];

    return {id: 'header-menu-internal', content: items};
  }

  private isPDMExportEnabled(): boolean {
    if (
      !this.systemConfigurationService.currentConfiguration.pdmExportActive ||
      this.projectModel.isNewConfiguration ||
      !this.projectModel.currentProject ||
      this.projectModel.currentProject.isSharedReadOnly ||
      !this.projectModel.currentProject.isLatestVersion ||
      !this.userModel.checkPermissions(["FINALIZE_PROJECTS"], false)
    ) {
      return false;
    }
    return true;
  }

  private isPDMExportVisible(): boolean {
    return this.userModel.checkPermissions(["SAVE_PROJECTS", "FINALIZE_PROJECTS"], false);
  }

  private isCalculationsExportEnabled(): boolean {
    return !!(this.projectModel.currentProject && this.projectModel.currentProject.projectType);
  }

  private pdmExportClickHandler(): void {
    this.projectComponentPresenter.showChooseFinalizationActionPopup();
  }

  private isCalculationsExportVisible(): boolean {
    return (
      this.projectModel.currentProjectType !== "XCOM" &&
      this.userModel.checkPermissions(["EXPORT_TO_CALCULATION"], false)
    );
  }

  private async generateCalculationProjectRepresentation(): Promise<void> {
    if (await this.saveProjectService.saveProjectBeforeAction('main.message.request_save')) {
      const startCalculationApproved = await this.projectComponentPresenter.openEditConfigurationSettingsByUser('global.label.next');
      if (startCalculationApproved) {
        if (await this.saveProjectService.saveProjectBeforeAction('main.message.generate_calculation_export_save')) {
          this.startCalculationProjectRepresentation();
        }
      }
    }
  }

  private isCheckForDuplicatesEnabled(): boolean {
    return IsProjectType.markingSystem(this.projectModel.currentProjectType);
  }

  private isCheckForDuplicatesVisible(): boolean {
    return (
      IsProjectType.markingSystem(this.projectModel.currentProjectType) &&
      this.userModel.checkPermissions(["SEARCH_DUPLICATE_ARTICLE_NUMBER_FOR_MARKING_SYSTEM"], false)
    );
  }

  private checkForDuplicates(): void {
    const currentProject: ProjectVO = this.projectModel.currentProject;
    currentProject.representations = [];
    this.markingSystemDataService.searchDuplicateArticleNumberForMarkingSystem(currentProject).subscribe({
      next: (result: ProjectFinalizationInfoVO) => {
        this.checkForDuplicatesSuccess(result);
      },
      error: (error) => {
        this.alertHelper.showError('global.title.error', 'global.message.unexpected_error');
      }
    });
  }

  private startCalculationProjectRepresentation(): void {
    this.overlayService.requestOverlayShow(OverlayBackground.color);
    this.representationService.scheduleUpdateForCadExport().subscribe(() => {
      this.saveCompositeArticleService.updateCompositeConnectionCorridor(this.projectModel.currentProject);
      this.calculationProjectExportService.exportProjectToCalculation(this.projectModel.currentProject);
      this.overlayService.requestOverlayHide(OverlayBackground.color);
    });
  }

  private checkForDuplicatesSuccess(projectFinalizationInfoVO: ProjectFinalizationInfoVO): void {
    let articleNumber: string | null = null;
    let productionPlants: string = '';
    const duplicateArticleNumbers = projectFinalizationInfoVO.duplicateArticleNumbers ?? [];
    for (const duplicateArticleNumber of duplicateArticleNumbers) {
      if (!duplicateArticleNumber) continue;
      let productionPlant: string | undefined = undefined;
      if (!articleNumber) {
        articleNumber = duplicateArticleNumber.articleNumber;
        productionPlant = duplicateArticleNumber.productionPlant;
      } else if (duplicateArticleNumber.articleNumber === articleNumber) {
        productionPlant = duplicateArticleNumber.productionPlant;
      }
      if (!StringUtils.isEmpty(productionPlant)) {
        if (!StringUtils.isEmpty(productionPlants)) {
          productionPlants = productionPlants + ', ';
        }
        productionPlants = productionPlants + productionPlant;
      }
    }
    if (articleNumber) {
      this.alertHelper.showAlert(
        'main.title.duplicate_found',
        'main.message.duplicate_found',
        {0: articleNumber, 1: productionPlants},
        [AlertButton.OK],
        "error"
      );
    }
  }

  private async resetForPdmExport(): Promise<void> {
    const result = await this.alertHelper.showAlert("header.menu.reset_for_pdm_export", "configuration.finalization.reset.question", {}, [AlertButton.NO, AlertButton.YES]);
    if (result === AlertButton.YES) {
      try {
        await this.projectFinalizaionService.resetCurrentProjectForPdmExport();
        this.toastrService.success(this.localeResourceManager.getValueByKey('toast.message.saved'));
      } catch (error) {
        Log.error(`Error resetting project for PDM export`, error);
      }
    }
  }

  private isResetForPdmExportEnabled(): boolean {
    const currentProject: ProjectVO = this.projectModel.currentProject;
    return !!currentProject
      && currentProject.isLatestVersion
      && currentProject.materialStatus >= 80
  }

  private isVisibleForPdmExportVisible(): boolean {
    return this.userModel.checkPermissions(["SAVE_PROJECTS", "FINALIZE_PROJECTS"], false) && this.isResetForPdmExportEnabled();
  }
}
